<template>
  <div class="min-h-screen bg-[#FAF8F8] px-4 sm:px-6 lg:px-8 py-12">
    <div class="max-w-4xl mx-auto space-y-10">

      <!-- En-tête -->
      <div class="text-center space-y-3">
        <div
          class="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-purple-50 text-xs font-bold text-[#7C3AED]">
          {{ t.badge }}
        </div>
        <h1 class="text-3xl sm:text-4xl font-extrabold text-[#0B1B3D] tracking-tight">
          {{ t.titlePrefix }} <span
            class="text-transparent bg-clip-text bg-gradient-to-r from-[#7C3AED] via-[#C026D3] to-[#F97316]">GiraafFlow</span>
        </h1>
        <p class="text-gray-500 text-sm sm:text-base max-w-xl mx-auto leading-relaxed">
          {{ t.subtitle }}
        </p>
      </div>

      <!-- Carte Vision & Piliers -->
      <div class="bg-white rounded-3xl p-8 border border-gray-100 shadow-xs space-y-8">
        <div>
          <h2 class="text-xl font-bold text-[#0B1B3D]">{{ t.missionTitle }}</h2>
          <p class="text-sm text-gray-600 leading-relaxed mt-2">
            {{ t.missionDesc }}
          </p>
        </div>

        <div class="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-6 border-t border-gray-100">

          <!-- Pilier 1 : Rapidité -->
          <div class="space-y-2">
            <div
              class="w-10 h-10 rounded-xl bg-purple-50 text-[#7C3AED] flex items-center justify-center border border-purple-100">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h3 class="font-bold text-[#0B1B3D] text-sm">{{ t.pillar1Title }}</h3>
            <p class="text-xs text-gray-500 leading-relaxed">
              {{ t.pillar1Desc }}
            </p>
          </div>

          <!-- Pilier 2 : Écosystème -->
          <div class="space-y-2">
            <div
              class="w-10 h-10 rounded-xl bg-orange-50 text-[#F97316] flex items-center justify-center border border-orange-100">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <h3 class="font-bold text-[#0B1B3D] text-sm">{{ t.pillar2Title }}</h3>
            <p class="text-xs text-gray-500 leading-relaxed">
              {{ t.pillar2Desc }}
            </p>
          </div>

          <!-- Pilier 3 : Sécurité -->
          <div class="space-y-2">
            <div
              class="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-700 flex items-center justify-center border border-emerald-100">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h3 class="font-bold text-[#0B1B3D] text-sm">{{ t.pillar3Title }}</h3>
            <p class="text-xs text-gray-500 leading-relaxed">
              {{ t.pillar3Desc }}
            </p>
          </div>

        </div>
      </div>

      <!-- Action Retour Dashboard -->
      <div class="text-center pt-2">
        <router-link to="/" style="background: linear-gradient(135deg, #7C3AED 0%, #C026D3 50%, #F97316 100%);"
          class="inline-block text-white font-bold text-xs sm:text-sm px-6 py-3 rounded-xl shadow-md hover:opacity-95 transition">
          {{ t.backBtn }} →
        </router-link>
      </div>

    </div>
  </div>
</template>

<script>
const ABOUT_TRANSLATIONS = {
  fr: {
    badge: 'Suite Giraaf Park Hub',
    titlePrefix: 'À propos de',
    subtitle: "La plateforme unifiée conçue pour planifier, exécuter et automatiser les livrables d'équipe avec fluidité et précision.",
    missionTitle: 'Notre Mission',
    missionDesc: "GiraafFlow élimine la friction entre la conception, la gestion de projet et la livraison technique. Grâce à une architecture moderne et une interface pensée pour la productivité, nous offrons aux agences, startups et équipes tech un espace de travail collaboratif rapide et fiable.",
    pillar1Title: 'Rapidité & Clarté',
    pillar1Desc: 'Des flux épurés, un Kanban temps réel et des tableaux de bord synthétiques.',
    pillar2Title: 'Écosystème IA',
    pillar2Desc: 'Intégration directe avec GiraafChat et outils intelligents de Giraaf Park.',
    pillar3Title: 'Sécurité Robuste',
    pillar3Desc: 'Authentification sécurisée JWT et persistance fiabilisée sur API Rails.',
    backBtn: 'Retourner au tableau de bord'
  },
  en: {
    badge: 'Giraaf Park Hub Suite',
    titlePrefix: 'About',
    subtitle: 'The unified platform designed to plan, execute, and automate team deliverables with speed and precision.',
    missionTitle: 'Our Mission',
    missionDesc: 'GiraafFlow removes friction between ideation, project management, and technical delivery. Built with modern architecture for maximum productivity, we empower tech agencies and teams with a fast, collaborative workspace.',
    pillar1Title: 'Speed & Clarity',
    pillar1Desc: 'Clean workflows, real-time Kanban, and actionable high-level dashboards.',
    pillar2Title: 'AI Ecosystem',
    pillar2Desc: 'Native connection with GiraafChat and Giraaf Park intelligent tools.',
    pillar3Title: 'Robust Security',
    pillar3Desc: 'Hardened JWT authentication and dependable Rails API persistence.',
    backBtn: 'Back to dashboard'
  }
}

export default {
  name: 'AboutView',
  data() {
    return {
      currentLang: localStorage.getItem('giraaf_lang') || 'fr'
    }
  },
  computed: {
    t() {
      return ABOUT_TRANSLATIONS[this.currentLang] || ABOUT_TRANSLATIONS.fr
    }
  },
  mounted() {
    window.addEventListener('storage', this.syncLang)
  },
  unmounted() {
    window.removeEventListener('storage', this.syncLang)
  },
  methods: {
    syncLang() {
      this.currentLang = localStorage.getItem('giraaf_lang') || 'fr'
    }
  }
}
</script>
